/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-routers components
import { Link } from "react-router-dom";

import { useState, useEffect } from "react";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

// sweetalert2 components
import Swal from "sweetalert2";

import { getAuthorizationHeader } from "Utils/helpers";
import { getData } from "Utils/server";

function ProfileInfoCard({
  title,
  description,
  status,
  info,
  social,
  action,
  elementsActions,
  editHandler,
  refreshProfileInfo,
}) {
  const labels = [];
  const values = [];
  const { socialMediaColors } = colors;
  const { size } = typography;
  const [enableEditing, setEnableEditing] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [rolesOptions, setRolesOptions] = useState([]);
  const [activeProfile, setActiveProfile] = useState(false);

  // Convert this form `objectKey` of the object key in to this `object key`
  Object.keys(info).forEach((el) => {
    if (el.match(/[A-Z\s]+/)) {
      const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
      const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);

      labels.push(newElement);
    } else {
      labels.push(el);
    }
  });

  // Push the object values into the values array
  Object.values(info).forEach((el) => values.push(el));

  // Render the card social media icons
  const renderSocial = social
    ? social.map(({ link, icon, color }) => (
        <SoftBox
          key={color}
          component="a"
          href={link}
          target="_blank"
          rel="noreferrer"
          fontSize={size.lg}
          color={socialMediaColors[color].main}
          pr={1}
          pl={0.5}
          lineHeight={1}
        >
          {icon}
        </SoftBox>
      ))
    : null;

  async function initData() {
    const rolesOptionsTmp = await getRolesData();
    setRolesOptions(rolesOptionsTmp);
  }

  async function getRolesData() {
    let rolesOptions = [];
    let getRolesEndpoint = `/api/v1/roles`;

    let response = await getData(getRolesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      rolesOptions = response.data.map((role) => {
        return { value: role.id, label: role.name };
      });
    }
    return rolesOptions;
  }

  // Render the card info items
  function renderProfile(data) {
    return Object.keys(data).map((key) => (
      <SoftBox
        key={key + " - holder - " + data[key]}
        py={1}
        pr={2}
        sx={{ display: !enableEditing ? "flex" : "-webkit-inline-box", width: "100%" }}
        className="input_line_holder"
      >
        <SoftBox
          sx={{
            display: elementsActions && elementsActions.find((el) => el.tab == key) ? "flex" : "",
            width:
              elementsActions && elementsActions.find((el) => el.tab == key)
                ? "90%"
                : "fit-content",
          }}
        >
          <SoftTypography variant="button" fontWeight="bold" textTransform="capitalize">
            {key}: &nbsp;
          </SoftTypography>
          <SoftTypography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              width:
                elementsActions && elementsActions.find((el) => el.tab == key)
                  ? "80%"
                  : "fit-content",
              display:
                elementsActions && elementsActions.find((el) => el.tab == key)
                  ? "block"
                  : enableEditing
                  ? "none !important"
                  : "",
            }}
          >
            &nbsp;{data[key]}
          </SoftTypography>
        </SoftBox>
        <SoftInput
          id={key + " - input - " + data[key]}
          key={key + " - input - " + data[key]}
          type={key == "Roles" ? "password" : "text"}
          placeholder={data[key]}
          size="small"
          defaultValue={data[key]}
          onChange={(e) => changeData(e, key)}
          sx={{
            width: "max-content !important",
            display:
              elementsActions && elementsActions.find((el) => el.tab == key)
                ? "none !important"
                : enableEditing
                ? key == "roles"
                  ? "none !important"
                  : ""
                : "none !important",
          }}
        ></SoftInput>
        <Grid
          sx={{
            display: enableEditing ? (key == "roles" ? "" : "none !important") : "none !important",
          }}
        >
          <SoftSelect
            id={key}
            key={key + " - select - " + data[key]}
            size="small"
            defaultValue={rolesOptions.find((item) => item.label == data[key])}
            options={rolesOptions}
            onChange={(e) => changeRole(e, key)}
          />
        </Grid>
        <SoftButton
          size="small"
          color="primary"
          variant="text"
          sx={{
            display: elementsActions && elementsActions.find((el) => el.tab == key) ? "" : "none",
          }}
          onClick={(e) =>
            editHandler(
              e,
              elementsActions.find((el) => el.tab == key)
            )
          }
        >
          change
        </SoftButton>
      </SoftBox>
    ));
  }

  const handleEnableEditing = () => {
    if (enableEditing) setEnableEditing(false);
    if (!enableEditing) setEnableEditing(true);
  };

  const resetData = () => {
    let datachanged = JSON.stringify(inputValues) != JSON.stringify(info);

    if (datachanged) {
      Swal.fire({
        title: `Cancel User Update?`,
        html: `You are going to lose some data while update User. <br />
                  Are you sure?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          let profileData = JSON.parse(JSON.stringify(inputValues));
          setInputValues(profileData);
          handleEnableEditing();
          editHandler(new Event("Custom Cancel"), { id: 100, action: "cancel", data: datachanged });
        } else {
        }
      });
    } else {
      let profileData = JSON.parse(JSON.stringify(inputValues));
      setInputValues(profileData);
      handleEnableEditing();
      editHandler(new Event("Custom Cancel"), { id: 100, action: "cancel", data: datachanged });
    }
  };

  const saveData = () => {
    let datachanged = JSON.stringify(inputValues) != JSON.stringify(info);
    datachanged = datachanged ? datachanged : status != activeProfile;
    if (datachanged) {
      handleEnableEditing();
      let dataToSent = JSON.parse(JSON.stringify(info));
      let role = rolesOptions.find((item) => item.label == info.roles);

      dataToSent.role_id = role.value;
      dataToSent.status = activeProfile;

      editHandler(new Event("Custom Save"), { id: 200, action: "save", data: dataToSent });
    } else {
      let profileData = JSON.parse(JSON.stringify(inputValues));
      setInputValues(profileData);
      handleEnableEditing();
      editHandler(new Event("Custom Cancel"), { id: 100, action: "cancel", data: datachanged });
    }
  };

  function changeData(e, data) {
    e.preventDefault();

    info[data] = e.currentTarget.value;
  }

  function changeRole(e, data) {
    info[data] = e.label;
  }

  useEffect(() => {
    if (Object.keys(info).length > 0) {
      let profileData = JSON.parse(JSON.stringify(info));
      setInputValues(profileData);
    }
  }, [info]);

  useEffect(() => {
    if (status != null) {
      setActiveProfile(status);
    }
  }, [status]);

  useEffect(() => {
    if (refreshProfileInfo) {
      initData();
    }
  }, [refreshProfileInfo]);

  useEffect(() => {
    initData();
  }, []);

  return (
    <Card>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
        <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {title}
        </SoftTypography>
        <SoftTypography
          component={Link}
          to={action.route}
          variant="body2"
          color="secondary"
          sx={{ display: action.route ? (enableEditing ? "none" : "") : "none" }}
          onClick={handleEnableEditing}
        >
          <Tooltip title={action.tooltip} placement="top">
            <Icon>edit</Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
      <SoftBox p={2} sx={{ paddingTop: "0px !important" }}>
        <SoftBox mb={2} lineHeight={1} sx={{ display: description ? "" : "none" }}>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            {description}
          </SoftTypography>
        </SoftBox>
        <SoftBox opacity={0.3}>
          <Divider />
        </SoftBox>
        <SoftBox
          justifyContent="right"
          alignItems="center"
          pt={2}
          px={2}
          sx={{ display: status == null ? "none" : "flex" }}
        >
          <SoftTypography
            variant="button"
            color="text"
            fontWeight="regular"
            sx={{ paddingRight: "5px", opacity: activeProfile ? "0.3" : "1" }}
          >
            Inactive
          </SoftTypography>
          <Switch
            color="secondary"
            checked={activeProfile}
            disabled={!enableEditing}
            onChange={() => setActiveProfile(!activeProfile)}
          />
          <SoftTypography
            variant="button"
            color="text"
            fontWeight="regular"
            sx={{ paddingLeft: "5px", opacity: activeProfile ? "1" : "0.3" }}
          >
            Active
          </SoftTypography>
        </SoftBox>
        <SoftBox>
          {inputValues ? renderProfile(inputValues) : ""}
          <SoftBox display="flex" py={1} pr={2} style={{ display: renderSocial ? "" : "none" }}>
            <SoftTypography variant="button" fontWeight="bold" textTransform="capitalize">
              social: &nbsp;
            </SoftTypography>
            {renderSocial}
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <SoftBox
        p={2}
        display={editHandler ? (enableEditing ? "flex" : "none") : "none"}
        justifyContent="right"
        alignItems="flex-end"
        sx={{ borderTop: 1, borderColor: "divider", paddingTop: "24px" }}
      >
        <SoftButton variant="gradient" color="light" size="small" onClick={resetData}>
          cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="dark"
          size="small"
          sx={{ marginLeft: "10px" }}
          onClick={saveData}
        >
          update
        </SoftButton>
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the ProfileInfoCard
ProfileInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  status: PropTypes.bool,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  social: PropTypes.arrayOf(PropTypes.object),
  action: PropTypes.shape({
    route: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
  }).isRequired,
  elementsActions: PropTypes.array,
  editHandler: PropTypes.func,
  refreshProfileInfo: PropTypes.bool,
};

export default ProfileInfoCard;
